import React from 'react';
import { Info, Layout, Type, FileText, DollarSign, Target, Square } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const ResumeAdvantageChecklist = () => {
  const ChecklistSection = ({ icon: Icon, title, items }) => (
    <div className="bg-blue-50 p-6 rounded-lg mb-6">
      <div className="flex items-start gap-4">
        <Icon className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <div className="w-full">
          <h3 className="font-semibold mb-4">{title}</h3>
          <ul className="space-y-4">
            {items.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
    <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
      {/* Header */}
      <h1 className="text-4xl font-bold mb-8">2025 Resume Advantage Critical Checklist</h1>

      {/* Info Box */}
      <div className="bg-gray-100 p-6 rounded-lg mb-12">
        <div className="flex items-start gap-3 mb-4">
          <Info className="w-5 h-5 mt-1" />
          <div>
            <h2 className="font-medium mb-2">Essential Overview</h2>
            <p className="text-gray-700">
              Your resume is a marketing document designed to secure interviews. Use this checklist
              to ensure your resume stands out from millions of other job seekers and significantly 
              increases your interview chances.
            </p>
          </div>
        </div>
      </div>

      {/* Layout Section */}
      <ChecklistSection
        icon={Layout}
        title="Document Structure"
        items={[
          "Length: 1 page for every ten years of experience (maximum 3 pages)",
          "Margins: Maximum 1.0 inch on all sides, minimum 0.3 inches",
          "Stack name and contact information vertically to save space",
          "Remove any 'Objective' section - it's outdated and limits opportunities",
          "Keep summary section (if included) to maximum six lines"
        ]}
      />

      {/* Typography Section */}
      <ChecklistSection
        icon={Type}
        title="Typography Guidelines"
        items={[
          "Set body text to minimum 11pt font size",
          "Set headings to 12-14pt maximum",
          "Use modern fonts: Garamond, Arial, Calibri, or Georgia",
          "Replace Times New Roman if currently using it"
        ]}
      />

      {/* Top Third Strategy */}
      <ChecklistSection
        icon={Target}
        title="The 'Million Dollar' Top Third"
        items={[
          "Optimize the top third to maximize impact",
          "Clearly convey who you are and your value proposition",
          "Add key certifications and graduate degrees next to your name",
          "Select most impressive certification if you have multiple"
        ]}
      />

      {/* Achievement Formatting */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <DollarSign className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">Achievement Presentation</h3>
            <ul className="space-y-4">
              {[
                "Start each bullet point with a number, $, or %",
                "Keep bullet points concise - avoid run-on sentences",
                "Focus on results and create curiosity"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>{item}</span>
                </li>
              ))}
              <div className="mt-4 bg-white p-4 rounded-lg">
                <p className="font-medium text-green-600 mb-2">✓ GOOD EXAMPLE:</p>
                <p className="mb-4">"67% increase in productivity by implementing unique industrial efficiency strategy"</p>
                <p className="font-medium text-red-600 mb-2">✗ POOR EXAMPLE:</p>
                <p className="text-sm">"Created a strategic game plan to include using efficiency modeling, and restructured the entire facility production line, including holding classes, updating training, re-aligning equipment, creating a new hire initiative, and onboarding new associates in the plan. We ultimately increased productivity by 67% and my leadership team was pleased."</p>
              </div>
            </ul>
          </div>
        </div>
      </div>

      {/* Final Tips */}
      <ChecklistSection
        icon={FileText}
        title="Critical Reminders"
        items={[
          "Keep paragraphs under six lines",
          "Maintain symmetrical margins throughout",
          "Tailor resume for each position",
          "Ensure consistent formatting throughout"
        ]}
      />
    </div>
    </DashboardLayout>
  );
};

export default ResumeAdvantageChecklist; 